import moment from "moment";
import React from "react";
import { TextArea, Message } from "semantic-ui-react";
import { Input, TimePicker } from "../../../components";
import { isDepartureTooEarly } from "../../flightvalidator";
import { CommercialFlightEditProps } from "../../model";
import AirlineSelection from "./airlineselection";
import AirportSelection from "./airportselection";
import BaseReactSelectDropdown from "components/dropdowns/reactselect/BaseReactSelectDropdown";
import { FilterDatePicker } from "components/DatePicker/SingleDatePicker";

interface Props {
  segment: CommercialFlightEditProps;
  onSegmentChange: (newSegment: CommercialFlightEditProps) => void;
  frequentFlyerPrograms: server.dto.FrequentFlyerProgram[];

  noDepartureAirportError: boolean;
  noArrivalAirportError: boolean;
  noAirlineError: boolean;
  noFlightNumberError: boolean;
  noFlightCodeError: boolean;
  departureTooEarly: boolean;
}

export default class CommercialFlightEdit extends React.Component<Props> {

  onTimeChange = (selectedTimeMoment: moment.Moment, current: moment.Moment, onChange: (arrival: moment.Moment) => void) => {
    const newMoment = current.clone().startOf("day")
      .add(selectedTimeMoment.hours(), "hours")
      .add(selectedTimeMoment.minutes(), "minutes");

    onChange(newMoment);
  };

  onDepartureTimeChange = (selectedTimeMoment: moment.Moment) => {
    this.onTimeChange(selectedTimeMoment, this.props.segment.departure, (newMoment) => this.onSegmentChange({ departure: newMoment }));
  };

  onArrivalTimeChange = (selectedTimeMoment: moment.Moment) => {
    this.onTimeChange(selectedTimeMoment, this.props.segment.arrival, (newMoment) => this.onSegmentChange({ arrival: newMoment }));
  };

  onSegmentChange = (newSegment: Partial<CommercialFlightEditProps>) => {
    const newProps = { ...this.props.segment, ...newSegment };
    this.props.onSegmentChange(newProps);
  };

  onAirlineChange = (airline: server.dto.Airline) => {
    this.onSegmentChange({ airline });
  }

  public render(): JSX.Element {

    const { arrival, departure, arrivalAirport, departureAirport, arrivalTerminal, departureTerminal, frequentFlyer,
      arrivalGate, departureGate, airline, flightCode, flightNumber, aircraft, seats, travelClass, eticket } = this.props.segment;

    const departureTooEarly = isDepartureTooEarly(departure, arrival);

    return <>
      <table className="s1_table bordergrid gray">
        <tbody>
          <tr>
            <th /><th>{RESX.Flight.Departure}</th>
            <th>{RESX.Flight.Arrival}</th>
          </tr>
          <tr>
            <td className="label">{RESX.Flight.Departure} / {RESX.Flight.Arrival}</td>
            <td style={{ width: 385 }}>
              <div className="flex">
                <div className="margin-right-small">
                  <FilterDatePicker
                    dontUpdateOnInputChange
                    minDate={moment(serverReference.constants.appointment.minDate)}
                    maxDate={moment(serverReference.constants.appointment.maxDate)}
                    onChange={d => this.onSegmentChange({ departure: d })}
                    value={departure}
                  />
                </div>
                <div>
                  <TimePicker
                    value={departure}
                    onChange={this.onDepartureTimeChange}
                  />
                </div>
              </div>

              {departureTooEarly &&
                <Message color="red" className="margin-top-small">{RESX.Flight.ArrivalCannotBeBeforeDeparture}</Message>
              }
            </td>
            <td>
              <div className="flex">
                <div className="margin-right-small">
                  <FilterDatePicker
                    dontUpdateOnInputChange
                    minDate={moment(serverReference.constants.appointment.minDate)}
                    maxDate={moment(serverReference.constants.appointment.maxDate)}
                    onChange={d => this.onSegmentChange({ arrival: d })}
                    value={arrival}
                  />
                </div>
                <div>
                  <TimePicker
                    value={arrival}
                    onChange={this.onArrivalTimeChange}
                  />
                </div>
              </div>
            </td>
          </tr>
          <tr>
            <td className="label">{RESX.Flight.Airport}</td>
            <td><AirportSelection error={this.props.noDepartureAirportError} airport={departureAirport} onAirportSelected={a => this.onSegmentChange({ departureAirport: a })} /></td>
            <td><AirportSelection error={this.props.noArrivalAirportError} airport={arrivalAirport} onAirportSelected={a => this.onSegmentChange({ arrivalAirport: a })} /></td>
          </tr>
          <tr>
            <td className="label">{RESX.Flight.Terminal}</td>
            <td>
              <Input
                value={departureTerminal}
                onChange={(_, { value }) => this.onSegmentChange({ departureTerminal: value })}
                maxLength={serverReference.constants.flight.maxTerminalLength}
                size="small"
                style={{ width: 60 }}
              />
            </td>
            <td>
              <Input
                value={arrivalTerminal}
                onChange={(_, { value }) => this.onSegmentChange({ arrivalTerminal: value })}
                maxLength={serverReference.constants.flight.maxTerminalLength}
                size="small"
                style={{ width: 60 }}
              />
            </td>
          </tr>
          <tr>
            <td className="label">{RESX.Flight.Gate}</td>
            <td >
              <Input
                value={departureGate}
                onChange={(_, { value }) => this.onSegmentChange({ departureGate: value })}
                maxLength={serverReference.constants.flight.maxGateLength}
                size="small"
                style={{ width: 60 }}
              />
            </td>
            <td>
              <Input
                value={arrivalGate}
                onChange={(_, { value }) => this.onSegmentChange({ arrivalGate: value })}
                maxLength={serverReference.constants.flight.maxGateLength}
                size="small"
                style={{ width: 60 }}
              />
            </td>
          </tr>
          <tr>
            <td className="label">{RESX.Flight.Airline}</td>
            <td colSpan={2}>
              <AirlineSelection error={this.props.noAirlineError} airline={airline} onAirlineChange={this.onAirlineChange} />
            </td>
          </tr>
          <tr>
            <td className="label">{RESX.Flight.FlightNumber}</td>
            <td colSpan={2}>
              <Input
                type="text"
                required={true}
                error={flightCode === "" ? this.props.noFlightCodeError : false}
                maxLength={3}
                placeholder="KL"
                value={flightCode}
                onChange={(_, { value }) => this.onSegmentChange({ flightCode: value })}
                style={{ width: 60, marginRight: 5 }
                } />
              <Input
                type="text"
                required={true}
                maxLength={4}
                error={flightNumber === "" ? this.props.noFlightNumberError : false}
                value={flightNumber}
                restrict={{
                  allowDigits: true,
                  allowLetters: false,
                  allowUnderscore: false
                }}
                onChange={(_, { value }) => this.onSegmentChange({ flightNumber: value })}
                placeholder={"687"}
                style={{ width: 60 }} />
            </td>
          </tr>
          <tr>
            <td className="label">{RESX.Flight.Aircraft}</td>
            <td colSpan={2}>
              <Input
                fluid
                type="text"
                maxLength={serverReference.constants.flight.maxAircraftLength}
                value={aircraft}
                onChange={(_, { value }) => this.onSegmentChange({ aircraft: value })} />
            </td>
          </tr>
          <tr>
            <td className="label">{RESX.Flight.Seats}</td>
            <td colSpan={2}>
              <Input
                fluid
                type="text"
                maxLength={serverReference.constants.flight.maxSeatsLength}
                value={seats}
                onChange={(_, { value }) => this.onSegmentChange({ seats: value })} />
            </td>
          </tr>
          <tr>
            <td className="label">{RESX.Flight.TravelClass}</td>
            <td colSpan={2}>
              <BaseReactSelectDropdown
                value={travelClass}
                onChange={tc => this.onSegmentChange({ travelClass: tc })}
                options={serverReference.flightTravelClasses}
                getLabel={tc => tc.text}
                getValue={tc => tc.value}
                excludeKeys={[]}
                config={{ isSearchable: true }}
              />
            </td>
          </tr>
          <tr>
            <td className="label">{RESX.Flight.FrequentFlyer}</td>
            <td colSpan={2}>
              <BaseReactSelectDropdown
                value={frequentFlyer}
                onChange={ff => this.onSegmentChange({ frequentFlyer: ff })}
                options={this.props.frequentFlyerPrograms}
                getLabel={ff => ff.name}
                getValue={ff => ff}
                excludeKeys={[]}
                config={{ isSearchable: true }}
              />
            </td>
          </tr>
          <tr>
            <td className="label">E-Ticket</td>
            <td colSpan={2}>
              <TextArea
                maxLength={serverReference.constants.flight.maxETicketLength}
                rows={4}
                onChange={(_, { value }) => this.onSegmentChange({ eticket: value.toString() })}
                value={eticket}
              />
            </td>
          </tr>
        </tbody>
      </table>
    </>;
  }
}